<template>
  <NTab
    v-gid="{
      module: 'portfolio',
      screen: 'home',
      section: 'portfolioVisualizationTabs',
      component: 'virtualPortfolioTabButton',
    }"
    rounded="full"
    :title="t('modules.portfolio.virtualPortfolio.title')"
    class="flex-none h-full"
    @click="handleVirtualPortfolioVisualizationClick"
  >
    <div class="bg-neutral-secondary h-full rounded-md px-2 pt-2">
      <div class="flex items-center justify-between mb-1">
        <NButtonToggleGroup
          v-model="virtualPortfolioVisualizationType"
          color="gray"
          dir="rtl"
          class="mx-auto"
        >
          <template
            v-for="visualization of VIRTUAL_PORTFOLIO_VISUALIZATION"
            :key="visualization.value"
          >
            <NButtonToggle
              v-gid="{
                module: 'portfolio',
                screen: 'home',
                section: 'virtualPortfolioVisualizationTab',
                component: visualization.gidComponentName,
              }"
              :label="visualization.label"
            >
              <template #icon>
                <component :is="visualization.icon" size="16" />
              </template>
            </NButtonToggle>
          </template>
        </NButtonToggleGroup>

        <NButton
          v-gid="{
            module: 'portfolio',
            screen: 'home',
            section: 'portfolioVisualizationTabs',
            component: 'createVirtualPortfolioButton',
          }"
          variant="flat"
          size="mini"
          class="inline-flex items-center absolute left-2"
          @click="openCreateVirtualPortfolioItemDialog"
        >
          <NegarPlusOutlineIcon size="12" />

          <NText class="pr-1" variant="overline">
            {{ t('modules.portfolio.virtualPortfolio.addNewSymbol') }}
          </NText>
        </NButton>
      </div>

      <Error
        v-if="isErrorVirtualPortfolioSymbols"
        @retry="getVirtualPortfolio"
      />

      <div
        v-else-if="isLoadingVirtualPortfolioSymbols"
        class="w-full h-full flex items-center justify-center"
      >
        <NLoading class="text-brand-primary" />
      </div>

      <EmptyList
        v-else-if="!virtualPortfolioSymbols.length"
        :title="t('modules.portfolio.virtualPortfolio.emptyPortfolioTitle')"
        :body="t('modules.portfolio.virtualPortfolio.emptyPortfolioMessage')"
        title-class="text-center mt-3"
        class="h-full flex flex-col justify-center items-center bg-neutral-primary rounded-md"
      >
        <template #image>
          <Image src="empty_box" alt="not_found" class="w-28 h-max" />
        </template>

        <NButton
          v-gid="{
            module: 'portfolio',
            screen: 'home',
            section: 'virtualPortfolioVisualizationTab',
            component: 'createVirtualPortfolioItemButton',
          }"
          variant="flat"
          size="small"
          class="inline-flex items-center"
          @click="openCreateVirtualPortfolioItemDialog"
        >
          <NText class="pr-1" variant="caption-1" weight="bold">
            {{ t('modules.portfolio.virtualPortfolio.addNewSymbol') }}
          </NText>
        </NButton>
      </EmptyList>

      <div v-else class="h-[calc(100%-90px)]">
        <div
          v-if="
            virtualPortfolioVisualizationType ===
            VIRTUAL_PORTFOLIO_VISUALIZATION.table.value
          "
          class="flex flex-col w-full h-full"
        >
          <VirtualPortfolioTable />
        </div>

        <div
          v-else-if="
            virtualPortfolioVisualizationType ===
            VIRTUAL_PORTFOLIO_VISUALIZATION.map.value
          "
          class="flex flex-col w-full h-full"
        >
          <PortfolioMarketMap :portfolio-map="virtualPortfolioMap" />
        </div>

        <div
          v-else
          class="bg-neutral-primary rounded-md h-full overflow-y-auto"
        >
          <PortfolioDonutChart :symbols="virtualPortfolioSymbols" />
        </div>

        <PortfolioSummary :portfolio-summary="virtualPortfolioSummary" />
      </div>
    </div>
  </NTab>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import {
  useLiveVirtualPortfolioQuery,
  useVirtualPortfolioMap,
} from '@core/portfolio';
import { useI18n } from 'vue-i18n';
import {
  NegarDonutIcon,
  NegarMarketMapIcon,
  NegarBurgerMenuIcon,
  NegarPlusOutlineIcon,
} from '@negar/icons';
import { useRoute, useRouter } from 'vue-router';

import {
  PortfolioDonutChart,
  PortfolioMarketMap,
  usePortfolioDialogs,
  PortfolioSummary,
  VirtualPortfolioTable,
} from '@/modules/portfolio';

import EmptyList from '@/components/EmptyList.vue';
import Error from '@/components/Error.vue';
import Image from '@/components/Image.vue';

const { t } = useI18n();

const router = useRouter();
const route = useRoute();

const virtualPortfolioVisualizationType = ref(0);

const {
  virtualPortfolioMap,
  isErrorVirtualPortfolioSymbols,
  getVirtualPortfolio,
  isLoadingVirtualPortfolioSymbols,
} = useVirtualPortfolioMap({
  immediate: computed(() => Boolean(route.query.virtualPortfolio)),
});
const { virtualPortfolioStats, symbols: virtualPortfolioSymbols } =
  useLiveVirtualPortfolioQuery({
    immediate: computed(() => Boolean(route.query.virtualPortfolio)),
  });

const { openCreateVirtualPortfolioItemDialog } = usePortfolioDialogs();

const virtualPortfolioSummary = computed(() => ({
  ...virtualPortfolioStats.value,
  quantity: virtualPortfolioSymbols.value.length,
}));

const VIRTUAL_PORTFOLIO_VISUALIZATION = {
  table: {
    value: 0,
    label: t('misc.table'),
    icon: NegarBurgerMenuIcon,
    gidComponentName: 'virtualPortfolioTableButton',
  },
  map: {
    value: 1,
    label: t('modules.portfolio.map'),
    icon: NegarMarketMapIcon,
    gidComponentName: 'virtualPortfolioMapButton',
  },
  donut: {
    value: 2,
    label: t('modules.portfolio.pieChart'),
    icon: NegarDonutIcon,
    gidComponentName: 'virtualPortfolioDonutChartButton',
  },
};

const handleVirtualPortfolioVisualizationClick = () => {
  const newQuery = { ...route.query };

  delete newQuery.portfolio;

  router.push({ query: { ...newQuery, virtualPortfolio: 'true' } });
};
</script>
